import { useReducer, useEffect, useCallback, useMemo } from 'react'
import _ from 'lodash'
import { EditCropContext } from './edit-crop-context'
import { EditCropReducer, initialEditCropState, EDIT_CROP_ACTION_TYPES } from './edit-crop-context-reducer'
import { useCropDetails } from '~/crop/use-crop-details'
import { useUpdateExistingCrop } from '~/crop/crop-modal/hooks/use-update-existing-crop'
import { supabaseGetter } from '~/api/supabase-getter'
import { supabase } from '~/supabase-client'
import { useCropsContextDispatch, cropEdited } from '~/common/context/crops/crops-context'
import { useCurrentUser } from '~/common/use-current-user'

/**
 * EditCropContextProvider
 *
 * Manages inline edit state for crop rows in the main timeline.
 * Wraps CropScheduleCard to provide editing context to both the
 * Y-axis (label chip + popper) and SVG content (per-row drag handles).
 */
export const EditCropContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(EditCropReducer, initialEditCropState)
  const {
    editingCropId,
    variety,
    count,
    successions,
    notes,
    seedCount,
    seedSuccessions,
    seedVarietyId,
    seedNotes,
    speciesTemplate,
    adjustedPhases,
    adjustedTasks,
    adjustedSuccessionData,
    isSubmitting,
  } = state

  const cropsDispatch = useCropsContextDispatch()
  const { currentUser } = useCurrentUser()

  // Fetch crop details when editing starts
  const { cropDetails: editingCropDetails, loading: editingCropDetailsLoading } = useCropDetails({
    cropId: editingCropId,
  })

  // Resolve species template from crop_details.template_id
  useEffect(() => {
    if (!editingCropDetails?.template_id) {
      dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SPECIES_TEMPLATE, speciesTemplate: null })
      dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_VARIETY, variety: null })
      return
    }

    async function resolveTemplates() {
      const { data: loadedTemplate } = await supabaseGetter.cropTemplates.getById(
        editingCropDetails.template_id
      )
      if (!loadedTemplate) return

      if (loadedTemplate.parent_template_id) {
        const { data: parentTemplate } = await supabaseGetter.cropTemplates.getById(
          loadedTemplate.parent_template_id
        )
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SPECIES_TEMPLATE, speciesTemplate: parentTemplate ?? null })
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_VARIETY, variety: loadedTemplate })
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SEED_VARIETY_ID, varietyId: loadedTemplate.id })
      } else {
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SPECIES_TEMPLATE, speciesTemplate: loadedTemplate })
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_VARIETY, variety: null })
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SEED_VARIETY_ID, varietyId: null })
      }
    }

    resolveTemplates()
  }, [editingCropDetails?.template_id])

  // Seed count when crop details load
  useEffect(() => {
    if (editingCropDetails?.count != null) {
      dispatch({ type: EDIT_CROP_ACTION_TYPES.SEED_COUNT, count: editingCropDetails.count })
    }
  }, [editingCropDetails?.count])

  // Seed notes when crop details load
  useEffect(() => {
    async function seedNotes() {
      if (editingCropDetails?.notes !== undefined) {
        dispatch({ type: EDIT_CROP_ACTION_TYPES.SEED_NOTES, notes: editingCropDetails.notes ?? '' })
      }
    }
    seedNotes()
  }, [editingCropDetails?.notes])

  // Seed successions count from actual child crop records
  useEffect(() => {
    async function loadSuccessionCount() {
      if (!editingCropId) return
      const { data: children } = await supabaseGetter.crops.getSuccessionChildrenByParent(editingCropId)
      dispatch({ type: EDIT_CROP_ACTION_TYPES.SEED_SUCCESSIONS, successions: children?.length ?? 1 })
    }
    loadSuccessionCount()
  }, [editingCropId])

  // Construct species object for useUpdateExistingCrop
  const species = useMemo(() => {
    if (!speciesTemplate || !editingCropDetails) return null
    return {
      id: speciesTemplate.id,
      name: editingCropDetails.species_name,
      family_code: editingCropDetails.family_code,
    }
  }, [speciesTemplate, editingCropDetails])

  const archetype = useMemo(() => {
    if (!editingCropDetails?.archetype_code) return null
    return { code: editingCropDetails.archetype_code }
  }, [editingCropDetails?.archetype_code])

  const { updateExistingCrop } = useUpdateExistingCrop({
    species,
    variety,
    archetype,
    count,
    growthType: editingCropDetails?.growth_code ?? null,
    lifecycleType: editingCropDetails?.lifecycle_code ?? null,
    notes,
    orgId: editingCropDetails?.organization_id ?? null,
  })

  const startEditing = useCallback((cropId) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.START_EDITING, cropId })
  }, [])

  const stopEditing = useCallback(() => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.STOP_EDITING })
  }, [])

  const setVariety = useCallback((val) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_VARIETY, variety: val })
  }, [])

  const setCount = useCallback((val) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_COUNT, count: val })
  }, [])

  const setSuccessions = useCallback((val) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SUCCESSIONS, successions: val })
  }, [])

  const setNotes = useCallback((val) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_NOTES, notes: val })
  }, [])

  const onPhaseAdjust = useCallback((phases) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.PHASE_ADJUST, phases })
  }, [])

  const onTaskAdjust = useCallback((tasks) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.TASK_ADJUST, tasks })
  }, [])

  const onSuccessionPhaseAdjust = useCallback((childId, phases) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SUCCESSION_PHASE_ADJUST, childId, phases })
  }, [])

  const onSuccessionTaskAdjust = useCallback((childId, tasks) => {
    dispatch({ type: EDIT_CROP_ACTION_TYPES.SUCCESSION_TASK_ADJUST, childId, tasks })
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!editingCropId || !species || !archetype) return

    dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SUBMITTING, isSubmitting: true })
    try {
      // Update crop record + rebuild adjusted tasks
      await updateExistingCrop({
        cropId: editingCropId,
        tasks: adjustedTasks ?? null,
      })

      // Persist adjusted phases if user dragged phase boundaries
      if (adjustedPhases) {
        const { data: schedule } = await supabaseGetter.cropSchedules.getActiveByCrop(editingCropId)
        const scheduleId = schedule?.id
        if (scheduleId) {
          await supabase.from('crop_phases').delete().eq('schedule_id', scheduleId)
          await supabase.from('crop_phases').insert(
            adjustedPhases.map(p => ({
              ..._.pick(p, ['phase_type_code', 'est_started_at', 'est_duration', 'step']),
              schedule_id: scheduleId,
            }))
          )
          supabaseGetter.cropPhases.invalidate()
          supabaseGetter.cropSchedules.invalidate()
        }
      }

      // Persist adjusted succession child phases/tasks
      if (adjustedSuccessionData && adjustedSuccessionData.size > 0) {
        const orgId = editingCropDetails?.organization_id

        // Resolve parent's first phase start for succession_interval_days calculation
        let parentFirstPhaseStart = adjustedPhases?.[0]?.est_started_at ?? null
        if (!parentFirstPhaseStart) {
          const { data: parentSchedule } = await supabaseGetter.cropSchedules.getActiveByCrop(editingCropId)
          if (parentSchedule?.id) {
            const { data: parentPhaseRows } = await supabaseGetter.cropPhases.getBySchedule(parentSchedule.id)
            parentFirstPhaseStart = parentPhaseRows?.[0]?.est_started_at ?? null
          }
        }

        for (const [childId, { phases, tasks }] of adjustedSuccessionData) {
          if (phases && phases.length > 0) {
            const { data: childSchedule } = await supabaseGetter.cropSchedules.getActiveByCrop(childId)
            const childScheduleId = childSchedule?.id
            if (childScheduleId) {
              await supabase.from('crop_phases').delete().eq('schedule_id', childScheduleId)
              await supabase.from('crop_phases').insert(
                phases.map(p => ({
                  ..._.pick(p, ['phase_type_code', 'est_started_at', 'est_duration', 'step']),
                  schedule_id: childScheduleId,
                }))
              )
            }
            // Upsert succession_interval_days to reflect new germination start
            if (parentFirstPhaseStart) {
              const intervalDays = Math.round(
                (new Date(phases[0].est_started_at) - new Date(parentFirstPhaseStart)) / (1000 * 60 * 60 * 24)
              )
              if (intervalDays > 0) {
                await supabase.from('crops').update({ succession_interval_days: intervalDays }).eq('id', childId)
              }
            }
          }

          if (tasks && tasks.length > 0 && orgId && currentUser) {
            // Only delete/replace non-completed tasks — preserve completed task records
            const tasksToInsert = tasks.filter(t => t.state !== 'completed')
            await supabase.from('tasks').delete().eq('crop_id', childId).neq('state', 'completed')
            if (tasksToInsert.length > 0) {
              await supabase.from('tasks').insert(
                tasksToInsert.map(t => ({
                  ..._.pick(t, ['task_code', 'task_window_start', 'duration', 'state', 'notes', 'completed_by', 'completed_at']),
                  crop_id: childId,
                  organization_id: orgId,
                  created_by: currentUser.id,
                }))
              )
            }
          }
        }
        supabaseGetter.cropPhases.invalidate()
        supabaseGetter.cropSchedules.invalidate()
        supabaseGetter.tasks.invalidate()
        supabaseGetter.crops.invalidate()
      }

      // Handle succession count changes
      const { data: existingChildren } = await supabaseGetter.crops.getSuccessionChildrenByParent(editingCropId)

      const currentCount = existingChildren?.length ?? 0

      if (successions < currentCount) {
        // Remove excess succession children (highest succession number first)
        const toDelete = existingChildren.slice(successions).map(c => c.id)
        await supabase.from('crops').delete().in('id', toDelete)
      } else if (successions > currentCount) {
        // Add new succession children based on the last existing child's data
        const lastChild = existingChildren?.[existingChildren.length - 1]
        const baseIntervalDays = lastChild && currentCount > 0
          ? Math.round((lastChild.succession_interval_days ?? currentCount * 14) / lastChild.succession)
          : 14

        // Fetch last child's phases and tasks to use as templates.
        // Use a direct schedule lookup (not getActiveByCrop) because succession child schedules
        // may lack season_id, causing the inner-join version to return null.
        let templatePhases = []
        let templateTasks = []
        if (lastChild) {
          const { data: lastChildScheduleRows } = await supabaseGetter.cropSchedules.getByCrop(lastChild.id)
          const lastChildScheduleId = lastChildScheduleRows?.[0]?.id ?? null
          if (lastChildScheduleId) {
            const { data: phases } = await supabaseGetter.cropPhases.getBySchedule(lastChildScheduleId)
            templatePhases = phases ?? []
          }
          const { data: allChildTasks } = await supabaseGetter.tasks.getByCrop(lastChild.id)
          templateTasks = (allChildTasks ?? []).filter(t => t.state !== 'completed')
        }

        const addDaysToISO = (isoStr, days) => {
          if (!isoStr) return isoStr
          const d = new Date(isoStr)
          d.setDate(d.getDate() + days)
          return d.toISOString()
        }

        const { data: parentSchedule } = await supabaseGetter.cropSchedules.getActiveByCrop(editingCropId)
        const orgId = editingCropDetails?.organization_id

        // When adding the very first succession (no existing children), fall back to
        // the parent crop's own phases/tasks as the template so new successions are not empty.
        if (!lastChild) {
          if (parentSchedule?.id) {
            const { data: parentPhases } = await supabaseGetter.cropPhases.getBySchedule(parentSchedule.id)
            templatePhases = parentPhases ?? []
          }
          if (orgId) {
            const { data: allParentTasks } = await supabaseGetter.tasks.getByCrop(editingCropId)
            templateTasks = (allParentTasks ?? []).filter(t => t.state !== 'completed')
          }
        }

        for (let i = currentCount; i < successions; i++) {
          const successionNum = i + 1
          const extraOffset = (successionNum - currentCount) * baseIntervalDays

          const { data: newChild, error: childErr } = await supabase.from('crops').insert({
            ..._.pick(editingCropDetails, [
              'organization_id', 'species_name', 'variety_name', 'name', 'count', 'yield',
            ]),
            created_by: currentUser.id,
            parent_crop_id: editingCropId,
            succession: successionNum,
            succession_interval_days: successionNum * baseIntervalDays,
          }).select().single()
          if (childErr) throw childErr

          const { error: childDetailsErr } = await supabase.from('crop_details').insert({
            ..._.pick(editingCropDetails, [
              'organization_id', 'template_id', 'archetype_code', 'family_code',
              'growth_code', 'lifecycle_code',
            ]),
            notes,
            crop_id: newChild.id,
          })
          if (childDetailsErr) throw childDetailsErr

          const { data: newSchedule, error: schedErr } = await supabase.from('crop_schedules').insert({
            crop_id: newChild.id,
            created_by: currentUser.id,
            season_id: parentSchedule?.season_id ?? null,
          }).select().single()
          if (schedErr) throw schedErr

          if (templatePhases.length > 0) {
            await supabase.from('crop_phases').insert(
              templatePhases.map(p => ({
                ..._.pick(p, ['phase_type_code', 'est_duration', 'step']),
                schedule_id: newSchedule.id,
                est_started_at: addDaysToISO(p.est_started_at, extraOffset),
              }))
            )
          }

          if (templateTasks.length > 0 && orgId) {
            await supabase.from('tasks').insert(
              templateTasks.map(t => ({
                ..._.pick(t, ['task_code', 'duration', 'notes']),
                crop_id: newChild.id,
                organization_id: orgId,
                created_by: currentUser.id,
                state: 'ready',
                season_id: parentSchedule?.season_id ?? null,
                task_window_start: addDaysToISO(t.task_window_start, extraOffset),
              }))
            )
          }
        }
        supabaseGetter.cropPhases.invalidate()
        supabaseGetter.cropSchedules.invalidate()
        supabaseGetter.tasks.invalidate()
        supabaseGetter.crops.invalidate()
      }

      // Trigger fresh re-fetch with all saved changes
      cropsDispatch(cropEdited({ cropId: editingCropId }))

      stopEditing()
    } catch (err) {
      console.error('Failed to save crop edits:', err)
      dispatch({ type: EDIT_CROP_ACTION_TYPES.SET_SUBMITTING, isSubmitting: false })
    }
  }, [
    editingCropId, species, archetype, successions, notes,
    updateExistingCrop, adjustedTasks, adjustedPhases, adjustedSuccessionData,
    editingCropDetails, currentUser, cropsDispatch,
    stopEditing,
  ])

  const hasEdits = useMemo(() => {
    if (adjustedPhases !== null) return true
    if (adjustedTasks !== null) return true
    if (adjustedSuccessionData !== null && adjustedSuccessionData.size > 0) return true
    if (seedCount !== null && count !== seedCount) return true
    if (seedSuccessions !== null && successions !== seedSuccessions) return true
    if (seedVarietyId !== undefined && variety?.id !== seedVarietyId) return true
    if (seedNotes !== null && notes !== seedNotes) return true
    return false
  }, [adjustedPhases, adjustedTasks, adjustedSuccessionData, count, seedCount, successions, seedSuccessions, variety, seedVarietyId, notes, seedNotes])

  const value = useMemo(() => ({
    editingCropId,
    editingCropDetails,
    editingCropDetailsLoading,
    speciesTemplate,
    variety,
    count,
    successions,
    notes,
    adjustedPhases,
    adjustedTasks,
    adjustedSuccessionData,
    isSubmitting,
    hasEdits,
    startEditing,
    stopEditing,
    setVariety,
    setCount,
    setSuccessions,
    setNotes,
    onPhaseAdjust,
    onTaskAdjust,
    onSuccessionPhaseAdjust,
    onSuccessionTaskAdjust,
    handleSubmit,
  }), [
    editingCropId, editingCropDetails, editingCropDetailsLoading,
    speciesTemplate, variety, count, successions, notes,
    adjustedPhases, adjustedTasks, adjustedSuccessionData, isSubmitting, hasEdits,
    startEditing, stopEditing, setVariety, setCount, setSuccessions, setNotes,
    onPhaseAdjust, onTaskAdjust, onSuccessionPhaseAdjust, onSuccessionTaskAdjust, handleSubmit,
  ])

  return (
    <EditCropContext.Provider value={value}>
      {children}
    </EditCropContext.Provider>
  )
}
